import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import HeroPowerdecker from '../components/HeroPowerdecker';

// Below-the-fold Komponenten lazy laden
const PreisSektion = lazy(() => import('../components/PreisSektion'));
const ReferenzenPreview = lazy(() => import('../components/ReferenzenPreview'));
const FAQ = lazy(() => import('../components/FAQ'));
const SeoTextBlock = lazy(() => import('../components/SeoTextBlock'));
const LeistungenUebersicht = lazy(() => import('../components/LeistungenUebersicht'));
const TrustStrip = lazy(() => import('../components/home/TrustStrip'));
const UmsetzungSection = lazy(() => import('../components/home/UmsetzungSection'));
const WarumGreenLion = lazy(() => import('../components/home/WarumGreenLion'));
const KombinationenSection = lazy(() => import('../components/home/KombinationenSection'));
const AblaufModern = lazy(() => import('../components/home/AblaufModern'));
const AbschlussCTA = lazy(() => import('../components/home/AbschlussCTA'));
const VideoSection = lazy(() => import('../components/home/VideoSection'));

// Intersection Observer Hook für verzögertes Laden
const useLazyVisible = () => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShow(true); obs.disconnect(); } },
      { rootMargin: '200px' }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, show];
};

// FAQ Schema Data für Google Rich Snippets
const faqSchemaData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Warum sollte ich Dachsanierung, Dämmung und Photovoltaik aus einer Hand machen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Weil alle drei Bereiche direkt zusammenhängen. Wenn Dach, Dämmung und Photovoltaik getrennt geplant werden, entstehen oft Schnittstellen, Missverständnisse oder spätere Probleme. Bei Green Lion Energy wird alles aufeinander abgestimmt: ein Ansprechpartner, ein Ablauf, klare Verantwortung."
      }
    },
    {
      "@type": "Question",
      "name": "Was kostet eine Dachsanierung in Delmenhorst oder Bremen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Die Kosten hängen von Dachfläche, Zustand und Umfang ab. Eine Neueindeckung beginnt ab ca. 15.000 €, eine Dachsanierung mit Dämmung ab ca. 25.000 € und eine Dachsanierung mit Photovoltaik ab ca. 35.000 €. Mit staatlicher Förderung von bis zu 20 % lassen sich die Kosten deutlich reduzieren."
      }
    },
    {
      "@type": "Question",
      "name": "Gibt es aktuell Förderungen für Dachsanierung und Dämmung?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ja. Für bestimmte Maßnahmen gibt es staatliche Förderungen von bis zu 20 % Zuschuss, maximal 12.000 €. Wir prüfen ehrlich, was förderfähig ist und übernehmen auf Wunsch die Antragsvorbereitung inkl. individuellem Sanierungsfahrplan (iSFP)."
      }
    },
    {
      "@type": "Question",
      "name": "Was passiert, wenn es während der Dachsanierung regnet?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ihr Dach ist zu keinem Zeitpunkt ungeschützt. Wir arbeiten in Abschnitten und sichern offene Flächen täglich mit Planen und Notabdeckungen. Wetter gehört zu unserem Handwerk – das Risiko tragen wir, nicht Sie."
      }
    },
    {
      "@type": "Question",
      "name": "Muss ich bei einer Dachsanierung alles im Voraus bezahlen?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Nein. Bei uns gibt es keine vollständige Vorkasse. Sie zahlen in fairen Raten nach Baufortschritt – die letzte Rate erst nach gemeinsamer Abnahme mit schriftlichem Abnahmeprotokoll."
      }
    },
    {
      "@type": "Question",
      "name": "In welchen Regionen ist Green Lion Energy tätig?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Green Lion Energy ist ein Dachdeckermeisterbetrieb mit Sitz in Delmenhorst. Wir betreuen Projekte in Delmenhorst, Bremen, Ganderkesee, Stuhr, Lemwerder, Hude, Oldenburg und der gesamten Region Weser-Ems."
      }
    }
  ]
};

const HomePage = () => {
  const [belowRef, showBelow] = useLazyVisible();
  return (
    <div className="pb-28 lg:pb-0">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchemaData)}</script>
      </Helmet>
      <HeroPowerdecker />
      <div ref={belowRef} className="mt-8 lg:mt-0">
        {showBelow && (
          <Suspense fallback={<div style={{ minHeight: '400px' }} />}>
            <TrustStrip />
            <LeistungenUebersicht />
            <VideoSection />
            <UmsetzungSection />
            <WarumGreenLion />
            <KombinationenSection />
            <ReferenzenPreview />
            <AblaufModern />
            <PreisSektion />
            <FAQ />
            <SeoTextBlock />
            <AbschlussCTA />
          </Suspense>
        )}
      </div>
    </div>
  );
};

export default HomePage;
